import { z } from 'zod';
import { DuelSchema, DuelsResponse, DuelsResponseSchema, DuelStatus, type DuelDto } from '@/types/duels';
import { apiRequest } from './client';

const duelLeagueSplitSchema = z.object({
  league_id: z.string().uuid(),
  p1_votes: z.number().int().nonnegative(),
  p2_votes: z.number().int().nonnegative(),
  voted: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
});

/** Coerces invalid `league_split` to `null` before `DuelSchema` parse (server may send partial objects). */
function normalizeLeagueSplitOnDuelPayload(raw: unknown): unknown {
  if (!raw || typeof raw !== 'object') {
    return raw;
  }
  const record = raw as Record<string, unknown>;
  if (!Object.prototype.hasOwnProperty.call(record, 'league_split')) {
    return raw;
  }
  const ls = record.league_split;
  if (ls === undefined || ls === null) {
    return { ...record, league_split: ls };
  }
  const parsed = duelLeagueSplitSchema.safeParse(ls);
  return {
    ...record,
    league_split: parsed.success ? parsed.data : null,
  };
}

function parseDuelResponse(raw: unknown): DuelDto {
  return DuelSchema.parse(normalizeLeagueSplitOnDuelPayload(raw));
}

export interface GetDuelsParams {
  is_active?: boolean; // default true
  status?: DuelStatus;
  gameweek_id?: string;
  featured_collection_slug?: string; // фильтр по featured-коллекции (slug)
  player_id?: string | string[]; // UUID игрока или массив для фильтрации по двум игрокам
  position?: number; // 1-GK, 2-DEF, 3-MID, 4-FWD
  team_id?: string; // UUID команды
  my_vote?: 'voted' | 'unvoted'; // фильтр по голосам пользователя
  my_duels?: boolean; // дуэли, созданные текущим пользователем (Bearer)
  order?: 'created_at.desc' | 'created_at.asc' | 'deadline_time.desc' | 'deadline_time.asc' | 'total_votes.desc' | 'total_votes.asc';
  limit?: number; // 1-100 default 50
  offset?: number; // default 0
  include_joins?: boolean;
  created_after?: string; // ISO 8601 — дуэли с created_at >= value
  fpl_team?: boolean; // дуэли с игроками из FPL-команды пользователя (Bearer)
  price_min?: number; // в десятых (40 = £4.0m)
  price_max?: number;
}

export async function getDuels(params: GetDuelsParams = {}): Promise<DuelsResponse> {
  const query = new URLSearchParams();

  if (params.is_active !== undefined) query.set('is_active', String(params.is_active));
  if (params.status) query.set('status', params.status);
  if (params.gameweek_id) query.set('gameweek_id', params.gameweek_id);
  if (params.featured_collection_slug) query.set('featured_collection_slug', params.featured_collection_slug);

  // Обработка player_id - может быть строкой или массивом
  if (params.player_id) {
    const playerIds = Array.isArray(params.player_id) ? params.player_id : [params.player_id];
    playerIds.forEach(id => query.append('player_id', id));
  }

  if (params.position !== undefined) query.set('position', String(params.position));
  if (params.team_id) query.set('team_id', params.team_id);
  if (params.my_vote) query.set('my_vote', params.my_vote);
  if (params.my_duels === true) query.set('my_duels', 'true');
  if (params.created_after) query.set('created_after', params.created_after);
  if (params.fpl_team === true) query.set('fpl_team', 'true');
  if (params.price_min !== undefined) query.set('price_min', String(params.price_min));
  if (params.price_max !== undefined) query.set('price_max', String(params.price_max));

  if (params.order) query.set('order', params.order);
  if (params.limit !== undefined) query.set('limit', String(params.limit));
  if (params.offset !== undefined) query.set('offset', String(params.offset));
  if (params.include_joins !== undefined) query.set('include_joins', String(params.include_joins));

  const qs = query.toString();
  const url = `/api/v1/duels${qs ? `?${qs}` : ''}`;

  const res = await apiRequest<unknown>({ url, method: 'GET' });

  try {
    const parsed = DuelsResponseSchema.parse(res);
    return parsed;
  } catch (error) {
    console.error('❌ Zod validation error:', error);
    throw error;
  }
}

export async function getDuelById(slug: string, opts?: { leagueId?: string }) {
  const sp = new URLSearchParams();
  sp.set('include_joins', 'true');
  if (opts?.leagueId) sp.set('league_id', opts.leagueId);
  const qs = sp.toString();
  const url = `/api/v1/duels/${encodeURIComponent(slug)}${qs ? `?${qs}` : ''}`;
  const res = await apiRequest<unknown>({ url, method: 'GET' });
  return parseDuelResponse(res);
}

export interface CreateDuelRequest {
  player_a_id: string; // UUID of player_a (DB id)
  player_b_id: string; // UUID of player_b (DB id)
  gameweek_id?: string; // optional
}

export interface CreateDuelResponse {
  duel_id: string;
}

export async function createDuel(body: CreateDuelRequest): Promise<CreateDuelResponse> {
  const res = await apiRequest<CreateDuelResponse>({
    url: '/api/v1/duels',
    method: 'POST',
    data: body,
  });
  return res;
}
